import {
  AuctionDTOBase,
  AuctionItemMediaDTO,
  MediaService
} from "../types/types";
import { sortMedia } from "./auctionItemUtils";

export type ImageSize = "sm" | "md" | "lg" | "xl";
export type ImageSizeWithOriginal = ImageSize | "original";
export type AspectRatio = "4/3" | "16/9";

export const getImageUrl = (
  key: string,
  size: ImageSizeWithOriginal,
  aspectRatio: AspectRatio,
  mediaService: MediaService = MediaService.AISAV2
) => {
  // Check if mediaService is exactly "AISA_V2" (string comparison)
  if (mediaService === MediaService.AISAV2) {
    return getImageUrlV2(key, size, aspectRatio);
  }
  const effectiveSize = size === "original" ? "xl" : size;
  return getImageUrlV1(key, effectiveSize, aspectRatio);
};

const getImageUrlV1 = (
  key: string,
  size: ImageSize,
  aspectRatio: AspectRatio
) => {
  const { width, height } = getImageDimensions(size, aspectRatio);
  const resizeObj = {
    resize: {
      width,
      height
    }
  };
  const imageRequestObject = {
    // @ts-ignore
    bucket: import.meta.env.VITE_IMAGES_BUCKET_NAME,
    key,
    edits: resizeObj
  };
  return (
    // @ts-ignore
    import.meta.env.VITE_IMAGES_BUCKET_URL +
    btoa(JSON.stringify(imageRequestObject))
  );
};

// 4/3
//   dealer, card          =  512 x 384, medium,
//   myyjä, image upload   =  512 x 384, medium
//   myyjä, active auction =  512 x 384, medium
//   dealer, kohdesivu     = 1024 x 768, large
// 16/9
//   sold cars mobiili     =  320 x 180, small
//   sold cars deskari     =  512 x 288, medium
// xl
//   lightbox              = 2000 x 1200 (fixed dimension, regardless of aspect ratio)
const IMAGE_WIDTHS = { sm: 320, md: 512, lg: 1024, xl: 2000 } as const;
const XL_HEIGHT = 1200; // xl size uses fixed 2000x1200 (actual file dimension in AWS)

const getImageDimensions = (
  size: ImageSize,
  aspectRatio: AspectRatio
): { width: number; height: number } => {
  const width = IMAGE_WIDTHS[size];

  // xl size always uses fixed 2000x1200 (actual file dimension in AWS)
  if (size === "xl") {
    return { width, height: XL_HEIGHT };
  }

  const height =
    aspectRatio === "4/3"
      ? Math.floor(width / 1.333333) // Using 4:3 aspect ratio
      : Math.floor(width / 1.777777); // Using 16/9 aspect ratio

  return { width, height };
};

export const getSuffix = (size: ImageSize, aspectRatio: AspectRatio) => {
  const { width, height } = getImageDimensions(size, aspectRatio);
  return `${width}x${height}`;
};

const getImageUrlV2 = (
  key: string,
  size: ImageSizeWithOriginal,
  aspectRatio: AspectRatio
) => {
  let finalSize = size;
  // Fallback for lg + 16/9 which is not supported directly
  if (size === "lg" && aspectRatio === "16/9") {
    finalSize = "xl";
  }

  const lastDotIndex = key.lastIndexOf(".");
  if (lastDotIndex === -1) {
    throw new Error("Invalid key. Missing file extension.");
  }
  const baseKey = key.substring(0, lastDotIndex);
  // Keep original extension case; AISA V2 assets may be case-sensitive on publish
  const extension = key.substring(lastDotIndex + 1);
  // AISA v2 derivatives are published at the root without the original/ prefix
  const filenameOnly = baseKey.split("/").pop() || baseKey;

  // @ts-ignore
  const bucketUrl = import.meta.env.VITE_IMAGES_BUCKET_URL_V2;

  if (finalSize === "original") {
    return `${bucketUrl}${filenameOnly}.${extension}`;
  }

  const validCombinations = [
    { size: "sm", ratio: "16/9" },
    { size: "md", ratio: "16/9" },
    { size: "md", ratio: "4/3" },
    { size: "lg", ratio: "4/3" },
    { size: "xl", ratio: "4/3" },
    { size: "xl", ratio: "16/9" }
  ];
  const isValid = validCombinations.some(
    (c) => c.size === finalSize && c.ratio === aspectRatio
  );
  if (!isValid) {
    throw new Error(
      `Invalid combination of size ${size} (effective: ${finalSize}) and aspect ratio ${aspectRatio}`
    );
  }

  const suffix = getSuffix(finalSize, aspectRatio);
  return `${bucketUrl}${filenameOnly}-${suffix}.${extension}`;
};

export const getAuctionImageUrl = (
  auction: AuctionDTOBase | null,
  imageIndex = 0,
  size: ImageSizeWithOriginal,
  aspectRatio: AspectRatio = "4/3",
  preferCoverImage = false
) => {
  if (
    !auction?.auctionItem?.media?.length ||
    imageIndex >= auction.auctionItem.media.length
  ) {
    return "/assets/images/missing_car.jpg";
  }

  const media = auction.auctionItem.media;
  let selectedIndex = imageIndex;

  // Add support for preferring cover (frontAngle) image
  if (preferCoverImage) {
    const frontAngleIndex = media.findIndex(
      (media) => media.from === "frontAngle"
    );
    if (frontAngleIndex !== -1) {
      selectedIndex = frontAngleIndex;
    }
  }

  return getImageUrl(
    media[selectedIndex].key,
    size,
    aspectRatio,
    media[selectedIndex].mediaService
  );
};

export const getAuctionImageUrls = (
  auction: AuctionDTOBase | null,
  size: ImageSizeWithOriginal
): string[] => {
  const imageUrls: string[] = [];
  if (auction && auction.auctionItem && auction.auctionItem.media) {
    // Sort media to ensure general_images come first
    const sortedMedia = [...auction.auctionItem.media].sort(sortMedia);
    for (const mediaItem of sortedMedia) {
      imageUrls.push(
        getImageUrl(
          mediaItem.key,
          size,
          "4/3",
          mediaItem.mediaService ?? MediaService.AISAV2
        )
      );
    }
  }
  return imageUrls;
};

export const getAuctionCardImage = (media: AuctionItemMediaDTO[]) => {
  if (!media || media.length === 0) {
    return "";
  }
  const firstImage = media[0];
  const imageKey = firstImage?.key ?? "";
  const mediaService = firstImage?.mediaService ?? MediaService.AISAV2;
  return getImageUrl(imageKey, "md", "4/3", mediaService);
};

export const checkImageAvailability = async (
  url: string,
  timeout = 2000
): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    const timeoutId = globalThis.setTimeout(() => {
      cleanup();
      resolve(false);
    }, timeout);

    const cleanup = () => {
      if (timeoutId) globalThis.clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
    };

    img.onload = () => {
      cleanup();
      resolve(true);
    };

    img.onerror = () => {
      cleanup();
      resolve(false);
    };

    img.src = url;
  });
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const waitForImage = async (
  url: string,
  maxAttempts = 20, // Up to ~60-80 seconds total timeout for bulk uploads
  initialInterval = 1000
): Promise<boolean> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const isAvailable = await checkImageAvailability(url);
    if (isAvailable) {
      return true;
    }

    if (attempt < maxAttempts - 1) {
      // Exponential backoff: 200ms, 400ms, 800ms, 1.6s, then cap at 2s for bulk uploads
      const interval = Math.min(initialInterval * Math.pow(2, attempt), 2000);
      await wait(interval);
    }
  }
  return false;
};
